import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Layout } from '../components/Layout'
import { hasAnyRole } from '../utils/permissions'
import { supabase } from '../services/supabase'
import { LogIn, Activity, Search } from 'lucide-react'

const UserLogs = () => {
  const { userProfile, loading: authLoading } = useAuth()
  const [loginLogs, setLoginLogs] = useState([])
  const [activityLogs, setActivityLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [users, setUsers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)

  useEffect(() => {
    if (userProfile) {
      if (hasAnyRole(userProfile, ['super_admin', 'admin'])) {
        fetchUsers()
      } else {
        // Müdür kendi loglarını görür
        fetchLogs(userProfile.id)
      }
    }
  }, [userProfile])

  useEffect(() => {
    if (selectedUserId) {
      fetchLogs(selectedUserId)
    } else if (hasAnyRole(userProfile, ['super_admin', 'admin']) && !selectedUserId && users.length > 0) {
      // Yönetici için kullanıcı seçilmediyse loading'i false yap
      setLoading(false)
    }
  }, [selectedUserId, userProfile, users])

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    )
  }

  if (!userProfile) {
    return <Navigate to="/login" replace />
  }

  const isAdmin = hasAnyRole(userProfile, ['super_admin', 'admin'])

  const fetchUsers = async () => {
    try {
      setUsersLoading(true)
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name, role:roles(display_name)')
        .order('full_name')

      if (error) throw error
      setUsers(data || [])
      setLoading(false) // Kullanıcılar yüklendi, artık loading false
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error)
      setLoading(false)
    } finally {
      setUsersLoading(false)
    }
  }

  const fetchLogs = async (userId) => {
    try {
      setLoading(true)

      // Son 10 giriş logu
      const { data: loginData, error: loginError } = await supabase
        .from('login_logs')
        .select('*')
        .eq('user_id', userId)
        .order('login_at', { ascending: false })
        .limit(10)

      if (loginError) throw loginError

      // Son 50 aktivite logu
      const { data: activityData, error: activityError } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50)

      if (activityError) throw activityError

      setLoginLogs(loginData || [])
      setActivityLogs(activityData || [])
    } catch (error) {
      console.error('Loglar yüklenemedi:', error)
      alert('Loglar yüklenirken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '-'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    if (hours > 0) {
      return `${hours}s ${minutes}d ${secs}sn`
    } else if (minutes > 0) {
      return `${minutes}d ${secs}sn`
    }
    return `${secs}sn`
  }

  const getActionLabel = (actionType) => {
    const labels = {
      'user_login': 'Giriş Yaptı',
      'user_logout': 'Çıkış Yaptı',
      'notification_created': 'Bildirim Oluşturdu',
      'notification_status_updated': 'Bildirim Durumu Güncelledi',
      'notification_archived': 'Bildirim Arşivledi',
      'notification_unarchived': 'Bildirim Arşivden Çıkarıldı',
      'school_category_created': 'Okul Kategorisi Oluşturdu',
      'school_category_updated': 'Okul Kategorisi Güncelledi',
      'school_category_deleted': 'Okul Kategorisi Sildi',
      'notification_category_created': 'Bildirim Kategorisi Oluşturdu',
      'notification_category_updated': 'Bildirim Kategorisi Güncelledi',
      'notification_category_deleted': 'Bildirim Kategorisi Sildi',
    }
    return labels[actionType] || actionType
  }

  const filteredActivityLogs = activityLogs.filter((log) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      log.description?.toLowerCase().includes(searchLower) ||
      getActionLabel(log.action_type).toLowerCase().includes(searchLower)
    )
  })

  if (loading && !isAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">Yükleniyor...</div>
      </Layout>
    )
  }

  // Yönetici için kullanıcı seçilmediyse bilgi mesajı göster
  if (isAdmin && !selectedUserId && !usersLoading) {
    return (
      <Layout>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Kullanıcı Logları</h1>
          <p className="text-gray-600 mt-1">Kullanıcı giriş ve aktivite loglarını görüntüleyin</p>
        </div>
        <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kullanıcı Seç
          </label>
          {usersLoading ? (
            <div className="text-center py-4">Kullanıcılar yükleniyor...</div>
          ) : (
            <select
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value || null)}
              className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Bir kullanıcı seçin</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name || user.email} ({user.role?.display_name})
                </option>
              ))}
            </select>
          )}
        </div>
        {!usersLoading && users.length === 0 && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Henüz kullanıcı bulunmamaktadır.</p>
          </div>
        )}
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Kullanıcı Logları</h1>
        <p className="text-gray-600 mt-1">
          {isAdmin ? 'Kullanıcı giriş ve aktivite loglarını görüntüleyin' : 'Giriş ve aktivite loglarınızı görüntüleyin'}
        </p>
      </div>

      {isAdmin && selectedUserId && (
        <div className="mb-6 bg-white shadow-sm border border-gray-200 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kullanıcı Seç
          </label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value || null)}
            className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bir kullanıcı seçin</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.full_name || user.email} ({user.role?.display_name})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Giriş Logları */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <LogIn size={20} className="text-blue-600" />
          Son 10 Giriş
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Giriş Zamanı
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Çıkış Zamanı
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Süre
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  IP Adresi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loginLogs.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    Giriş logu bulunamadı
                  </td>
                </tr>
              ) : (
                loginLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(log.login_at).toLocaleString('tr-TR')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.logout_at ? new Date(log.logout_at).toLocaleString('tr-TR') : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDuration(log.session_duration)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Aktivite Logları */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Activity size={20} className="text-blue-600" />
            Son 50 İşlem
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Ara..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Zaman
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  İşlem
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Açıklama
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  IP Adresi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredActivityLogs.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    Aktivite logu bulunamadı
                  </td>
                </tr>
              ) : (
                filteredActivityLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(log.created_at).toLocaleString('tr-TR')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {getActionLabel(log.action_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {log.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  )
}

export default UserLogs

